import {
  OPERATOR_INTEGRATIONS,
  type OperatorIntegration,
} from "@maito/shared/operatorIntegrations";

import copy from "./integration-pages.json" with { type: "json" };
import { workflowToolNames } from "./automationIdeaHubs.ts";
import { type AutomationIdea, automationIdeas } from "./automationIdeas.ts";
import { publicPage } from "./publicPages.ts";

/** Every AI assistant people use Maito from, for copy that lists where the work can start. */
export const aiAssistants = "Claude, ChatGPT, Cursor, Codex, Grok or Muse";

/** When the integration pages were last reviewed against the in-app catalog, as YYYY-MM-DD. */
export const integrationsUpdated = "2026-10-21";

type IntegrationCopy = {
  /** Public name, when the catalog's name is a tagline ("Fathom – Your Meeting…"). */
  readonly name?: string;
  /** URL slug, when the catalog id carries a suffix ("pipedrive-mcp"). */
  readonly slug?: string;
  /** Search title, when people search for the product by another name ("Jira"). */
  readonly title?: string;
  /** What the tool is, in one plain sentence. */
  readonly summary: string;
  /** Two requests someone could send Maito once it's connected. */
  readonly asks: readonly string[];
  /** Tool names workflows use for it, when they differ from the public name. */
  readonly workflowTools?: readonly string[];
  /** The slug of the category page it's listed on. */
  readonly category: string;
};

const pageCopy = copy as Record<string, IntegrationCopy | undefined>;

export type PublicIntegration = {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly title: string;
  readonly summary: string;
  readonly asks: readonly string[];
  readonly auth: OperatorIntegration["auth"];
  readonly publisher: string;
  readonly publisherUrl: string;
  readonly logo: string;
  /**
   * The MCP server address Maito connects to: what people searching "<tool> MCP" want. Null when
   * the address is hosted for Claude, which would read as Claude's connector and may not work
   * from other clients.
   */
  readonly serverUrl: string | null;
  readonly workflowTools: readonly string[];
  readonly category: string;
};

/**
 * Integrations that connect in one click in Maito and have public copy. The in-app catalog is
 * the source: an entry it hides, or one without copy yet, gets no public page.
 */
const priorityOrder = { P0: 0, P1: 1, P2: 2 };

export const publicIntegrations: readonly PublicIntegration[] = [...OPERATOR_INTEGRATIONS]
  // The tools most teams use come first in every list.
  .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
  .flatMap((entry) => {
    const page = pageCopy[entry.id];
    if (!page) return [];
    const name = page.name ?? entry.name;
    return [
      {
        id: entry.id,
        slug: page.slug ?? entry.id,
        name,
        // "<tool> MCP" outranks "<tool> integration" and "<tool> Slack integration" in search
        // volume for every tool sampled, at low difficulty. See
        // docs/plans/seo-data/dataforseo-integration-patterns.json.
        title: page.title ?? `${name} MCP: Use ${name} in Slack and Email`,
        summary: page.summary,
        asks: page.asks,
        auth: entry.auth,
        publisher: entry.publisher,
        publisherUrl: entry.publisherUrl,
        logo: `/integration-logos/${entry.logo}`,
        serverUrl: /claude\.(com|ai)|anthropic/i.test(entry.url) ? null : entry.url,
        workflowTools: page.workflowTools ?? [name],
        category: page.category,
      },
    ];
  });

type CategoryCopy = {
  readonly slug: string;
  /** Filter and chip label. */
  readonly name: string;
  /** The phrase the page targets, in sentence case: the heading and the search title. */
  readonly searchPhrase: string;
  readonly intro: string;
};

const categoryCopy: readonly CategoryCopy[] = [
  {
    slug: "crm",
    name: "CRM",
    searchPhrase: "CRM integrations",
    intro:
      "Keep the CRM current without asking reps to change how they work. Maito reads deals and contacts, logs calls and updates records when someone asks in Slack or by email.",
  },
  {
    slug: "prospecting",
    name: "Prospecting and outreach",
    searchPhrase: "Sales prospecting integrations",
    intro:
      "Find the right people, research their accounts and see how outreach is going, without opening five tabs.",
  },
  {
    slug: "scheduling-and-email",
    name: "Scheduling and email",
    searchPhrase: "Scheduling and email integrations",
    intro: "Check who booked, block time and catch the customer emails you haven't answered.",
  },
  {
    slug: "payments-and-finance",
    name: "Payments and finance",
    searchPhrase: "Payments and finance integrations",
    intro:
      "Overdue invoices, new revenue and missing receipts, answered from the source instead of an export.",
  },
  {
    slug: "accounting",
    name: "Accounting and finance",
    searchPhrase: "Accounting and finance integrations",
    intro:
      "Books, close, spend and tax questions, answered from the system of record instead of last month's export.",
  },
  {
    slug: "market-data",
    name: "Market and company data",
    searchPhrase: "Financial and market data integrations",
    intro:
      "Company profiles, filings, prices and deals, pulled into the brief or the report someone asked for.",
  },
  {
    slug: "legal",
    name: "Legal",
    searchPhrase: "Legal software integrations",
    intro:
      "Contracts, matters and case law, found and summed up before the meeting, not during it.",
  },
  {
    slug: "hr-and-recruiting",
    name: "HR and recruiting",
    searchPhrase: "HR and recruiting integrations",
    intro: "Candidates, open roles and people data, checked without opening another dashboard.",
  },
  {
    slug: "customer-support",
    name: "Customer support",
    searchPhrase: "Customer support integrations",
    intro:
      "Maito works next to your help desk. It reads tickets and conversations, finds the pattern across them, and checks the code and logs behind a report.",
  },
  {
    slug: "meetings",
    name: "Meetings and notes",
    searchPhrase: "Meeting notes integrations",
    intro:
      "What was promised on the call, who owns the next step, and what customers keep asking for. Maito reads your meeting notes so nobody has to rewatch the recording.",
  },
  {
    slug: "docs-and-files",
    name: "Docs and files",
    searchPhrase: "Docs and file storage integrations",
    intro: "Find the policy, the contract or the spec where it already lives, and answer from it.",
  },
  {
    slug: "project-management",
    name: "Project management",
    searchPhrase: "Project management integrations",
    intro:
      "Turn a Slack thread into a ticket, a meeting into tasks, and a board into a status update.",
  },
  {
    slug: "productivity",
    name: "Productivity",
    searchPhrase: "Productivity app integrations",
    intro:
      "Notes, lists and the everyday apps your team already lives in, ready when someone asks.",
  },
  {
    slug: "developer-tools",
    name: "Developer tools",
    searchPhrase: "Developer tool integrations",
    intro:
      "Code, APIs, databases and the apps your team builds on. Maito uses them to answer how things work and to ship changes for review.",
  },
  {
    slug: "monitoring-and-hosting",
    name: "Monitoring and hosting",
    searchPhrase: "Monitoring and hosting integrations",
    intro:
      "Errors, alerts, deploys and incidents. Maito reads them to find what broke and why, and reports back in the thread.",
  },
  {
    slug: "product-analytics",
    name: "Product analytics",
    searchPhrase: "Product analytics integrations",
    intro:
      "Ask how people use the product in plain words and get the number, not a dashboard to dig through.",
  },
  {
    slug: "design",
    name: "Design and diagrams",
    searchPhrase: "Design and diagram integrations",
    intro:
      "Build from a Figma frame, sum up a workshop board, or draw the diagram everyone keeps asking for.",
  },
  {
    slug: "marketing-analytics",
    name: "Marketing analytics",
    searchPhrase: "Marketing analytics integrations",
    intro:
      "Spend, conversions and creative results across every ad platform, in one report that shows up on Monday.",
  },
  {
    slug: "seo",
    name: "SEO and competitor research",
    searchPhrase: "SEO integrations",
    intro:
      "Rankings, keywords, backlinks and what competitors are doing, checked on a schedule instead of by hand.",
  },
  {
    slug: "social-media",
    name: "Social media",
    searchPhrase: "Social media management integrations",
    intro: "Write, schedule and report on posts, and keep up with the social inbox.",
  },
  {
    slug: "social-listening",
    name: "Social listening",
    searchPhrase: "Social listening integrations",
    intro: "What people say about you and your competitors, summed up before anyone asks.",
  },
  {
    slug: "content-and-video",
    name: "Content and video",
    searchPhrase: "Content and video integrations",
    intro: "Designs, decks, sites and videos, made or edited from a request in Slack or email.",
  },
  {
    slug: "email-marketing-and-surveys",
    name: "Email marketing and surveys",
    searchPhrase: "Email marketing and survey integrations",
    intro: "Newsletters, campaigns and survey results, drafted and summed up for you.",
  },
  {
    slug: "ecommerce",
    name: "Ecommerce and shipping",
    searchPhrase: "Ecommerce and shipping integrations",
    intro: "Orders, inventory, rates and late shipments, checked without logging in to each tool.",
  },
  {
    slug: "automation",
    name: "Automation",
    searchPhrase: "Automation integrations",
    intro:
      "Already built automations elsewhere? Maito can run them, check what failed and tell you why.",
  },
  {
    slug: "research",
    name: "Research and data",
    searchPhrase: "Research and data integrations",
    intro:
      "Web search, public datasets and academic sources, so answers come with where they came from.",
  },
  {
    slug: "life-sciences",
    name: "Life sciences and health",
    searchPhrase: "Life sciences and healthcare integrations",
    intro:
      "Papers, trials and biomedical databases, searched and summed up for the people who need them.",
  },
  {
    slug: "education",
    name: "Education and nonprofits",
    searchPhrase: "Education and nonprofit integrations",
    intro: "Courses, learning materials and nonprofit data, handled from Slack or email.",
  },
];

export type IntegrationCategory = CategoryCopy & {
  readonly integrations: readonly PublicIntegration[];
};

/** Categories with at least one integration; an empty one gets no page. */
export const integrationCategories: readonly IntegrationCategory[] = categoryCopy
  .map((category) => ({
    ...category,
    integrations: publicIntegrations.filter(
      (integration) => integration.category === category.slug,
    ),
  }))
  .filter((category) => category.integrations.length > 0);

export const integrationPath = (integration: Pick<PublicIntegration, "slug">) =>
  `/integrations/${integration.slug}`;

export const integrationCategoryPath = (category: Pick<IntegrationCategory, "slug">) =>
  `/integrations/category/${category.slug}`;

export const findIntegration = (slug: string) =>
  publicIntegrations.find((integration) => integration.slug === slug);

export const findIntegrationCategory = (slug: string) =>
  integrationCategories.find((category) => category.slug === slug);

export const integrationCategory = (integration: PublicIntegration) =>
  integrationCategories.find((category) => category.integrations.includes(integration));

export function integrationFromPath(path: string) {
  const match = /^\/integrations\/([a-z0-9-]+)$/.exec(path);
  return match ? findIntegration(match[1]!) : undefined;
}

export function integrationCategoryFromPath(path: string) {
  const match = /^\/integrations\/category\/([a-z0-9-]+)$/.exec(path);
  return match ? findIntegrationCategory(match[1]!) : undefined;
}

const normalize = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");

/** The integration a workflow tool name refers to, such as "Jira" → Atlassian. */
export const integrationForTool = (name: string) =>
  publicIntegrations.find((integration) =>
    integration.workflowTools.some((tool) => normalize(tool) === normalize(name)),
  );

/** Workflows that name this tool, so its page can show the jobs people hand Maito with it. */
export function integrationWorkflows(integration: PublicIntegration): AutomationIdea[] {
  const names = new Set(integration.workflowTools.map(normalize));
  return automationIdeas.filter((idea) =>
    workflowToolNames(idea).some((name) => names.has(normalize(name))),
  );
}

/** Other tools of the same kind, for "Also connect" links. */
export const relatedIntegrations = (integration: PublicIntegration) =>
  integrationCategory(integration)?.integrations.filter((other) => other !== integration) ?? [];

/** How connecting works, in the words the Connect dialog uses. */
export function connectSentence(integration: Pick<PublicIntegration, "auth" | "name">) {
  return integration.auth === "oauth"
    ? `You sign in to ${integration.name} in a new tab, and that's the whole setup.`
    : integration.auth === "header"
      ? `You paste a ${integration.name} API key. It stays encrypted, and Maito handles the rest.`
      : `It needs no separate sign-in: choose Connect and it's ready.`;
}

export type IntegrationFaq = { readonly question: string; readonly answer: string };

const sharingFaq: IntegrationFaq = {
  question: "Who on my team can use a connection?",
  answer:
    "You choose when you connect. Only me keeps it to your own chats. Workspace lets your teammates, shared chats and Loops use it. You can change it later.",
};

const limitFaq: IntegrationFaq = {
  question: "Can I limit what Maito does in a tool?",
  answer:
    "Yes. After you connect, turn off any tools you don't want Maito to use, such as the ones that change data. Your choice stays saved, and tools the service adds later stay off until you turn them on.",
};

const costFaq: IntegrationFaq = {
  question: "Do integrations cost extra?",
  answer:
    "No. Every integration is included in Maito's $149 per workspace per month, with unlimited people. You need your own account with each tool, and your AI provider bills its usage separately.",
};

/** Questions every integrations list page answers. */
export const integrationIndexFaqs: readonly IntegrationFaq[] = [
  {
    question: "What does connecting a tool let Maito do?",
    answer: `Work in it for you. Once a tool is connected, your agent can look things up and make changes there when someone asks in Slack, by email, or from ${aiAssistants}, and in Loops that run on a schedule.`,
  },
  {
    question: "How do I connect a tool?",
    answer:
      "Open Connections in your workspace, choose Connect, and sign in or paste an API key. Or just ask for the work: if your agent needs a tool that isn't connected, it sends you a link, and the work picks up after you sign in.",
  },
  {
    question: "What if my tool isn't listed?",
    answer:
      "Add it as your own MCP server: enter its address, then sign in or add a key. For a tool without an MCP server, save a skill with the steps and keep the login or API key in the Vault.",
  },
  sharingFaq,
  limitFaq,
  costFaq,
];

/** How the server signs in, as a clause: "it signs in with OAuth…". */
const signInClause = (integration: Pick<PublicIntegration, "auth">) =>
  integration.auth === "oauth"
    ? "it signs in with OAuth, so there's no key to copy"
    : integration.auth === "header"
      ? "it takes an API key or bearer token"
      : "it needs no sign-in";

/** Questions one integration's page answers: the server's facts first, then using it in Maito. */
export function integrationFaqs(integration: PublicIntegration): IntegrationFaq[] {
  const { name } = integration;
  return [
    integration.serverUrl
      ? {
          question: `What is the ${name} MCP server URL?`,
          answer: `${integration.publisher} publishes it at ${integration.serverUrl}, and ${signInClause(integration)}.`,
        }
      : {
          question: `Who publishes the ${name} MCP server?`,
          answer: `${integration.publisher}, and ${signInClause(integration)}. Maito connects to it for you, so there's no address to copy.`,
        },
    {
      question: `Can I use the ${name} MCP server from Slack?`,
      answer: `Yes. Connect it to Maito once, then mention Maito in any Slack channel or send it a direct message. The same connection works by email and from ${aiAssistants}.`,
    },
    {
      question: `Does everyone on my team have to connect ${name}?`,
      answer: `No. One person connects it once and chooses Workspace. Then everyone in the workspace, shared chats and Loops can use ${name}, without signing in themselves. Choose Only me instead to keep it to your own chats.`,
    },
    {
      question: `Do I have to connect ${name} before I ask?`,
      answer: `No. If a request needs ${name} and it isn't connected yet, your agent sends you a link. Connect it, and the work picks up where it stopped.`,
    },
    {
      question: `Can I limit what Maito does in ${name}?`,
      answer: `Yes. After you connect, turn off any ${name} tools you don't want Maito to use, such as the ones that change data. Tools ${name} adds later stay off until you turn them on.`,
    },
  ];
}

const faqPage = (faqs: readonly IntegrationFaq[]) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
});

const itemList = (name: string, integrations: readonly PublicIntegration[], origin: string) => ({
  "@type": "ItemList",
  name,
  numberOfItems: integrations.length,
  itemListElement: integrations.map((integration, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: integration.name,
    url: `${origin}${integrationPath(integration)}`,
  })),
});

const crumbs = (origin: string, items: readonly { name: string; path: string }[]) => ({
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Integrations", path: "/integrations" }, ...items].map(
    (item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${origin}${item.path}`,
    }),
  ),
});

/** Search identity for the main integrations page. */
export function integrationsIndexPage() {
  return {
    ...publicPage("/integrations"),
    updated: integrationsUpdated,
    structuredData: (origin: string) => [
      {
        "@type": "ItemList",
        name: "Maito integrations by category",
        numberOfItems: integrationCategories.length,
        itemListElement: integrationCategories.map((category, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: category.searchPhrase,
          url: `${origin}${integrationCategoryPath(category)}`,
        })),
      },
      faqPage(integrationIndexFaqs),
    ],
  };
}

const smallWords = new Set(["a", "and", "for", "in", "of", "or", "the", "to", "with"]);

/** "CRM integrations" → "CRM Integrations"; acronyms and small words stay as written. */
const titleCase = (phrase: string) =>
  phrase
    .split(" ")
    .map((word, index) =>
      index > 0 && smallWords.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");

/** "Salesforce, Attio and Pipedrive", or the first three and "More". */
const listNames = (integrations: readonly PublicIntegration[]) => {
  const names = integrations.map((integration) => integration.name);
  return names.length > 3
    ? `${names.slice(0, 3).join(", ")} and More`
    : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
};

/** Search identity for one category of integrations. */
export function integrationCategoryPage(category: IntegrationCategory) {
  const path = integrationCategoryPath(category);
  return {
    kind: "marketing" as const,
    path,
    title: `${titleCase(category.searchPhrase)}: ${listNames(category.integrations)}`,
    description: `Connect ${listNames(category.integrations).replace(/ More$/, " more")} to Maito. ${category.intro}`,
    updated: integrationsUpdated,
    structuredData: (origin: string) => [
      itemList(category.searchPhrase, category.integrations, origin),
      faqPage(integrationIndexFaqs.slice(1)),
      crumbs(origin, [{ name: category.name, path }]),
    ],
  };
}

/** Search identity for one integration's page. */
export function integrationPage(integration: PublicIntegration) {
  const path = integrationPath(integration);
  const category = integrationCategory(integration);
  return {
    kind: "marketing" as const,
    path,
    title: integration.title,
    shareLogo: integration.logo,
    shareTitle: `Use ${integration.name} in Slack and Email`,
    description: `Connect the ${integration.name} MCP server to Maito ${
      integration.auth === "oauth"
        ? "with one sign-in"
        : integration.auth === "header"
          ? "with an API key"
          : "in one click"
    }, and your whole team can use it from Slack or email: “${integration.asks[0]}”`,
    updated: integrationsUpdated,
    structuredData: (origin: string) => [
      faqPage(integrationFaqs(integration)),
      crumbs(origin, [
        ...(category ? [{ name: category.name, path: integrationCategoryPath(category) }] : []),
        { name: integration.name, path },
      ]),
    ],
  };
}
